import React, {useCallback, useContext, useState} from 'react';
import {useSelector} from 'react-redux';

import uniqueId from 'lodash.uniqueid';
import {DateRangeInput, FormInput, NumberInput, NumberInputTypes} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark/required-mark';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {useDateValidation} from 'src/hooks/useDateValidation';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {MAX_CONTRACT_FIELD_VALUE} from 'src/modules/profile/account/constants';
import {createMcpContract} from 'src/openapi/admin/apis/McpContractsApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Float, Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {Textarea} from '@pb/kit-component-textarea-v2';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';

import {selectMcpSaving} from '../../services/mcp-slice';

import {mcpAddContract} from './add-contract-modal.module.styl';


type Props = {
    accountId: number,
    onClose: () => void,
    onCreated: () => void,
};

export const AddContractModal = ({accountId, onClose, onCreated}: Props) => {
    const apiDispatch = useAppDispatch();
    const {mcpContrCreate} = useEventTracking();
    const saving = useSelector(selectMcpSaving);
    const {onCreateNote} = usePushNotification();
    const {locale} = useContext(LocaleContext);

    const [contractNumber, setContractNumber] = useState('');
    const [credits, setCredits] = useState<number | undefined>(undefined);
    const [overages, setOverages] = useState<number | undefined>(undefined);
    const [creditCost, setCreditCost] = useState<number | undefined>(undefined);
    const [overageCost, setOverageCost] = useState<number | undefined>(undefined);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [comment, setComment] = useState('');

    const {hasInvalidRange} = useDateValidation(startDate, endDate, setEndDate);

    const canSave = contractNumber
        && credits != null
        && overages != null
        && creditCost != null
        && overageCost != null
        && startDate
        && endDate
        && !hasInvalidRange;

    const onSave = useCallback(async () => {
        if (!canSave) {
            return;
        }

        const response = await apiDispatch(createMcpContract({
            accountId,
            mcpCreateContractRequest: {
                contractNumber,
                initialCredits: credits!,
                costPerCredit: creditCost!,
                initialOverageCredits: overages!,
                costPerOverageCredit: overageCost!,
                startDate: startDate!,
                endDate: endDate!,
                comment: comment || undefined,
            },
        }));

        if (response.payload.error) {
            onCreateNote({
                id: uniqueId('mcp-contract-create-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => response.payload.data?.message || 'Failed to create contract',
            });
        } else {
            mcpContrCreate();
            onCreated();
        }
    }, [
        canSave, apiDispatch, accountId, contractNumber,
        credits, creditCost, overages, overageCost,
        startDate, endDate, comment, onCreated, onCreateNote, mcpContrCreate,
    ]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Contract Details
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: 600}} className={mcpAddContract}>
                <Group gap="xl" direction="v">
                    <Item>
                        <Label><RequiredMark label="Contract #"/></Label>
                        <FormInput
                            value={contractNumber}
                            width={210}
                            maxLength={255}
                            onBlur={setContractNumber}
                        />
                    </Item>

                    <Group gap="l">
                        <Item>
                            <NumberInput
                                width={210}
                                inputType={NumberInputTypes.INTEGER}
                                label={<RequiredMark label="Credits"/>}
                                maxValue={MAX_CONTRACT_FIELD_VALUE}
                                value={credits}
                                displayedValue={credits != null ? Integer.format(credits, locale) : ''}
                                onBlur={value => setCredits(value != null ? +value : undefined)}
                            />
                        </Item>
                        <Item>
                            <NumberInput
                                width={210}
                                inputType={NumberInputTypes.INTEGER}
                                label={<RequiredMark label="Overages"/>}
                                maxValue={MAX_CONTRACT_FIELD_VALUE}
                                value={overages}
                                displayedValue={overages != null ? Integer.format(overages, locale) : ''}
                                onBlur={value => setOverages(value != null ? +value : undefined)}
                            />
                        </Item>
                    </Group>

                    <Group gap="l">
                        <Item>
                            <NumberInput
                                width={210}
                                inputType={NumberInputTypes.FLOAT}
                                label={<RequiredMark label="Credit Cost"/>}
                                placeholder="$, USD"
                                maxValue={MAX_CONTRACT_FIELD_VALUE}
                                value={creditCost}
                                displayedValue={creditCost != null ? Float.format(creditCost, locale) : ''}
                                onBlur={value => setCreditCost(value != null ? +value : undefined)}
                            />
                        </Item>
                        <Item>
                            <NumberInput
                                width={210}
                                inputType={NumberInputTypes.FLOAT}
                                label={<RequiredMark label="Overage Cost"/>}
                                placeholder="$, USD"
                                maxValue={MAX_CONTRACT_FIELD_VALUE}
                                value={overageCost}
                                displayedValue={overageCost != null ? Float.format(overageCost, locale) : ''}
                                onBlur={value => setOverageCost(value != null ? +value : undefined)}
                            />
                        </Item>
                    </Group>

                    <Group direction="v" gap="s">
                        <Typography fontWeight="semi-bold">Contract Term</Typography>
                        <Group direction="h" gap="l" alignItems="start">
                            <DateRangeInput
                                id="mcp-contract-start-date"
                                label={<RequiredMark label="Starts"/>}
                                selectRangeType="start"
                                startDate={startDate}
                                endDate={endDate}
                                onChange={setStartDate}
                                width={210}
                            />
                            <DateRangeInput
                                id="mcp-contract-end-date"
                                label={<RequiredMark label="Ends"/>}
                                selectRangeType="end"
                                startDate={startDate}
                                endDate={endDate}
                                onChange={setEndDate}
                                clearable={true}
                                width={210}
                            />
                        </Group>
                    </Group>

                    <Item>
                        <Label>Comment</Label>
                        <Textarea
                            value={comment}
                            resizable={true}
                            style={{height: 80}}
                            maxLength={255}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setComment(e.target.value)}
                        />
                    </Item>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group alignItems="center">
                    <Button variant="Type.PRIMARY" disabled={!canSave} busy={saving} onClick={onSave}>
                        Save
                    </Button>
                    <Button variant="Type.SECONDARY" busy={saving} onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
